import { FC, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { buildTemplatesUrl } from '@/shared/lib';

import { templatesModel } from '@/entities/templates';

import { DocumentTemplateForm } from '@/features/templateEditor';
import { TemplateSettingsDraft } from '@/features/templateEditor/config/types';

import styles from './TemplatesEditorPane.module.scss';

type TemplatesDocumentEditorLocationState = {
  initialSettings?: TemplateSettingsDraft;
};

type TemplatesDocumentEditorProps = {
  className?: string;
};

export const TemplatesDocumentEditor: FC<TemplatesDocumentEditorProps> = (
  props,
) => {
  const { className } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const templateId = searchParams.get('id') ?? undefined;
  const schemaId = searchParams.get('specialty') ?? undefined;
  const locationState =
    location.state as TemplatesDocumentEditorLocationState | null;
  const initialSettings = templateId
    ? undefined
    : locationState?.initialSettings;

  const template = useAppSelector(
    templateId
      ? templatesModel.selectors.selectDocumentTemplateById(templateId)
      : () => undefined,
  );

  const schema = useAppSelector(
    schemaId
      ? templatesModel.selectors.selectEncounterSchemaById(schemaId)
      : () => undefined,
  );

  const specialtyOptions = useAppSelector(
    templatesModel.selectors.selectAllEncounterSchemas,
  );

  const isTemplateByIdLoading = useAppSelector(
    templateId
      ? templatesModel.selectors.selectDocumentTemplateByIdLoading(templateId)
      : () => false,
  );
  const templatesError = useAppSelector(
    templatesModel.selectors.selectTemplatesError,
  );
  const isSchemasLoading = useAppSelector(
    templatesModel.selectors.selectEncounterSchemasLoading,
  );

  useEffect(() => {
    if (templateId) {
      void dispatch(templatesModel.fetchDocumentTemplateById(templateId));
    }
  }, [dispatch, templateId]);

  useEffect(() => {
    if (schemaId) {
      void dispatch(templatesModel.fetchEncounterSchemaById(schemaId));
    }
  }, [dispatch, schemaId]);

  const handleSaveComplete = useCallback(() => {
    navigate(buildTemplatesUrl({ specialty: schemaId }));
  }, [schemaId]);

  if (templateId && !template && !isTemplateByIdLoading && templatesError) {
    return (
      <div className={className}>
        <FormattedMessage
          id="templates.documentEditor.loadError"
          defaultMessage="Failed to load the template"
        />
      </div>
    );
  }

  // Show the placeholder only until the template is in the store. Autosave
  // navigates to the id of the version it just created, which is already
  // stored, and unmounting the form on that refetch would drop the edits made
  // in the meantime and the caret position.
  if ((templateId && !template) || (schemaId && !schema && isSchemasLoading)) {
    return (
      <div className={className}>
        <FormattedMessage id="global.loading" defaultMessage="Loading…" />
      </div>
    );
  }

  return (
    <DocumentTemplateForm
      className={className ?? styles.editor}
      template={template}
      initialSchemaId={schemaId ?? null}
      specialtyOptions={specialtyOptions}
      schemaLanguage={schema?.language}
      initialSettings={initialSettings}
      onSaveComplete={handleSaveComplete}
    />
  );
};
